import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import { Collapse, Row, Col } from 'antd';

import {
  AssetsSlider,
  AssetType,
  AssetPDFSubtype,
  AssetEntity,
  AssetImageSubtype,
} from 'src/modules/assets';
import { FormattedMessage } from 'react-intl';
import { LayoutOverlay } from 'src/modules/layout';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { AssetRow } from 'src/modules/products/components/downloads/AssetRow/AssetRow';
import { CategoryPanelHeader } from 'src/modules/products/components/downloads/CategoryPanelHeader/CategoryPanelHeader';
import { isPreviewSupported } from 'src/modules/assets/components/slider/AssetsSlider';
import styles from './ProductsDownloads.module.scss';
import { ProductEntity } from '../../types';
import { useLocale } from 'src/modules/locale';

interface ProductsDownloadsProps {
  product: ProductEntity;
  className?: string;
}

interface CategoryMap {
  [s: string]: AssetEntity[];
}

const ProductsDownloads: React.FunctionComponent<ProductsDownloadsProps> = (props) => {
  const { product } = props;
  const { outputChannel } = useLocale();

  const [activePreviewAsset, setActivePreviewAsset] = useState<AssetEntity>(null);
  const [previewAssetList, setPreviewAssetList] = useState<AssetEntity[]>(null);

  const assets = (product?.assets || [])
    .filter((a) => a.channels && a.channels.includes(`${outputChannel}_media`))
    .sort((a, b) => (a.file?.name < b.file?.name ? -1 : 1));

  const openPreview = (asset, assetListToPreview) => {
    setPreviewAssetList(assetListToPreview);
    setActivePreviewAsset(asset);
  };
  const closePreview = () => {
    setActivePreviewAsset(null);
    setPreviewAssetList(null);
  };

  const categories: CategoryMap = useMemo(() => {
    const c: CategoryMap = {
      cad: [],
      spaceTypes: [],
      sheets: [],
      certificates: [],
      images: [],
    };

    if (!_.isEmpty(assets)) {
      c.cad = assets.filter((asset) => asset.type === AssetType.CAD);
      c.spaceTypes = assets.filter(
        (asset) => asset.type === AssetType.PDF && asset.subtype === AssetPDFSubtype.SpaceType
      );
      c.sheets = assets.filter(
        (asset) =>
          asset.type === AssetType.PDF &&
          [
            AssetPDFSubtype.ProductSheet,
            AssetPDFSubtype.Instructions,
            AssetPDFSubtype.Maintenance,
            AssetPDFSubtype.PressRelease,
            AssetPDFSubtype.Zerti,
          ].includes(asset.subtype as AssetPDFSubtype)
      );
      c.certificates = assets.filter(
        (asset) =>
          [AssetType.PDF, AssetType.Image].includes(asset.type) &&
          asset.subtype === AssetPDFSubtype.Certificate
      );
      c.images = assets.filter(
        (asset) =>
          asset.type === AssetType.Image &&
          [
            AssetImageSubtype.Clipped,
            AssetImageSubtype.Cutout,
            AssetImageSubtype.Ambiance,
          ].includes(asset.subtype as AssetImageSubtype)
      );
    }

    return c;
  }, [assets]);

  return (
    <div className={props.className}>
      <LayoutOverlay display={!!activePreviewAsset} onClose={closePreview}>
        {!!activePreviewAsset && (
          <AssetsSlider activeAsset={activePreviewAsset} assets={previewAssetList} />
        )}
      </LayoutOverlay>

      <Row className={styles.header}>
        <Col span={12} className={styles.col}>
          <FormattedMessage id={`products/downloads/listing/file_category`} />
        </Col>
        <Col span={12} className={styles.col}>
          <FormattedMessage id={`products/downloads/listing/file_format`} />
        </Col>
      </Row>
      <Collapse
        className={styles.collapseContainer}
        bordered={false}
        defaultActiveKey={['panel-1']}
        expandIconPosition="right"
        expandIcon={({ isActive }) => (
          <GlobalIcon
            image={IconImage.DownArrow}
            className={classnames(
              styles.collapsePanelButtonIcon,
              isActive && styles.collapsePanelButtonIconActive
            )}
          />
        )}>
        {Object.entries(categories).map(
          ([categoryKey, categoryAssets]) =>
            categoryAssets.length > 0 && (
              <Collapse.Panel
                collapsible="header"
                header={<CategoryPanelHeader categoryKey={categoryKey} assets={categoryAssets} />}
                key={`panel-${categoryKey}`}>
                {categoryAssets.map((asset) => (
                  <AssetRow
                    key={asset.id}
                    asset={asset}
                    onPreviewClick={(asset) => openPreview(asset, categoryAssets)}
                    enablePreview={isPreviewSupported(asset)}
                    showMetadata={true}
                  />
                ))}
              </Collapse.Panel>
            )
        )}
      </Collapse>
    </div>
  );
};

export default ProductsDownloads;
